import { useCallback } from 'react';

import { prepareValuesForSubmission } from 'helpers/candidate_application/form-page-helpers';
import { useDispatch, useSelector } from 'react-redux';

import {
  saveCandidateChecksValues,
  selectCandidateApplication,
  updateCandidateValues,
  updateFormValues,
} from 'api/candidate-application-slice';

/**
 * Hook for preparing form values for submission
 */
export default function useFormSubmission() {
  const dispatch = useDispatch();
  const {
    candidateFields,
    application: { check_values },
  } = useSelector(selectCandidateApplication);
  const {
    sections = [],
    candidateValues = {},
    hiddenValues = {},
    formValues = {},
    sectionFieldPrefixes = {},
    fieldPathIndex = {},
    excludeOnSubmitCodes = [],
  } = candidateFields || {};

  const prepareForSubmission = useCallback(
    (watchValues, sectionKey, currentSubsectionPrefixes = null, existingValues = {}) => {
      return prepareValuesForSubmission(
        watchValues,
        candidateValues,
        hiddenValues,
        formValues, // Redux formValues from state
        sections,
        sectionKey,
        fieldPathIndex,
        excludeOnSubmitCodes,
        currentSubsectionPrefixes,
        sectionFieldPrefixes,
        existingValues,
      );
    },
    [
      sections,
      candidateValues,
      hiddenValues,
      formValues,
      sectionFieldPrefixes,
      fieldPathIndex,
      excludeOnSubmitCodes,
    ],
  );

  const saveFormValuesAndSubmit = useCallback(
    async (
      sectionKey,
      watchValues,
      token,
      systemValues = {},
      currentSubsectionPrefixes = null,
      rejectedValuesFix = false,
      consentsData = [],
    ) => {
      // Use the comprehensive submission process
      // prepareValuesForSubmission now handles preserving values from completed checks/extracted fields
      const existingValues = check_values?.values || {};
      const { updatedCandidateValues, updatedSectionValues } = prepareValuesForSubmission(
        watchValues,
        candidateValues,
        hiddenValues,
        formValues, // Redux formValues from state
        sections,
        sectionKey,
        fieldPathIndex,
        excludeOnSubmitCodes,
        currentSubsectionPrefixes,
        sectionFieldPrefixes,
        existingValues,
      );

      let updateRejectValues = null;
      let rejectedStatus = false;
      if (rejectedValuesFix) {
        updateRejectValues = check_values.rejected_values?.values?.filter(
          value => !(value in watchValues),
        );
        rejectedStatus =
          updateRejectValues?.length > 0 || check_values.rejected_values?.files?.length > 0
            ? true
            : false;
      }

      // Merge consents data - keep items from other subsections, replace items from current subsection(s)
      // This allows multi-page sections (like FCRA) to preserve data from previous pages
      let mergedConsents = null;
      if (consentsData?.length > 0) {
        const existingConsents = Array.isArray(check_values?.consents) ? check_values.consents : [];
        // Get unique subsection_codes from the new data
        const newSubsectionCodes = [...new Set(consentsData.map(item => item.subsection_code))];
        // Filter out items that have the same subsection_code as any in the new data
        const filteredExisting = existingConsents.filter(
          item => !newSubsectionCodes.includes(item.subsection_code),
        );
        mergedConsents = [...filteredExisting, ...consentsData];
      }

      // Save to backend
      await dispatch(
        saveCandidateChecksValues({
          token,
          data: {
            values: updatedCandidateValues,
            system_values: systemValues,
            ...(mergedConsents ? { consents: mergedConsents } : {}),
            ...(rejectedValuesFix
              ? {
                  rejected_fixes: { ...check_values.rejected_fixes, values: true },
                  ...(updateRejectValues
                    ? {
                        rejected_values: {
                          ...check_values.rejected_values,
                          values: updateRejectValues,
                        },
                      }
                    : {}),
                  rejected: rejectedStatus,
                }
              : {}),
          },
        }),
      );

      dispatch(updateCandidateValues({ updatedCandidateValues }));

      dispatch(updateFormValues({ sectionKey, formValues: updatedSectionValues }));
    },
    [
      dispatch,
      sections,
      candidateValues,
      hiddenValues,
      formValues,
      sectionFieldPrefixes,
      fieldPathIndex,
      excludeOnSubmitCodes,
      check_values,
    ],
  );

  return { prepareForSubmission, saveFormValuesAndSubmit };
}
